import React from 'react';
import {
  BarChart,
  Bar,
  PieChart,
  Pie,
  Cell,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer
} from 'recharts';
import FirebaseTimeRangeSelector from '../../components/analytics/FirebaseTimeRangeSelector';
import './GameEngagementChart.css';

const GameEngagementChart = ({ analytics, fullSize = false }) => {
  const { modeBreakdown, difficultyBreakdown, avgCompletionTime, avgMoves } = analytics;

  // Prepare mode data
  const modeData = Object.entries(modeBreakdown).map(([mode, count]) => ({
    mode: mode.charAt(0).toUpperCase() + mode.slice(1),
    games: count
  }));

  // Prepare difficulty data
  const difficultyData = Object.entries(difficultyBreakdown).map(([difficulty, count]) => ({
    difficulty: difficulty.charAt(0).toUpperCase() + difficulty.slice(1),
    games: count
  }));

  const COLORS = {
    Standard: '#3B82F6',  // Primary blue
    Daily: '#8B5CF6',     // Purple
    Memory: '#EC4899',    // Pink
    Easy: '#10B981',      // Green
    Medium: '#F59E0B',    // Orange
    Hard: '#EF4444'       // Red
  };

  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  return (
    <div className={`game-engagement-chart ${fullSize ? 'full-size' : ''}`}>
      <div className="chart-header">
        <h3>Game Engagement</h3>
        <FirebaseTimeRangeSelector />
      </div>

      <div className="engagement-metrics">
        <div className="metric">
          <div className="metric-value">{formatTime(avgCompletionTime)}</div>
          <div className="metric-label">Avg Time</div>
        </div>
        <div className="metric">
          <div className="metric-value">{avgMoves.toFixed(1)}</div>
          <div className="metric-label">Avg Moves</div>
        </div>
      </div>

      <div className="charts-container">
        <div className="chart-section">
          <h4>Games by Mode</h4>
          <ResponsiveContainer width="100%" height={fullSize ? 300 : 200}>
            <BarChart data={modeData}>
              <CartesianGrid strokeDasharray="3 3" stroke="#E5E7EB" />
              <XAxis dataKey="mode" tick={{ fill: '#6B7280', fontSize: 12 }} />
              <YAxis tick={{ fill: '#6B7280', fontSize: 12 }} />
              <Tooltip
                contentStyle={{
                  backgroundColor: 'white',
                  border: '1px solid #E5E7EB',
                  borderRadius: '8px',
                  boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)'
                }}
              />
              <Bar dataKey="games" fill="#3B82F6">
                {modeData.map((entry, index) => (
                  <Cell key={`cell-${index}`} fill={COLORS[entry.mode] || '#4CAF50'} />
                ))}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </div>

        {difficultyData.length > 0 && (
          <div className="chart-section">
            <h4>Games by Difficulty</h4>
            <ResponsiveContainer width="100%" height={fullSize ? 300 : 200}>
              <PieChart>
                <Pie
                  data={difficultyData}
                  cx="50%"
                  cy="50%"
                  labelLine={false}
                  label={({ difficulty, percent }) =>
                    `${difficulty} ${(percent * 100).toFixed(0)}%`
                  }
                  outerRadius={fullSize ? 100 : 70}
                  fill="#8884d8"
                  dataKey="games"
                >
                  {difficultyData.map((entry, index) => (
                    <Cell
                      key={`cell-${index}`}
                      fill={COLORS[entry.difficulty] || '#999'}
                    />
                  ))}
                </Pie>
                <Tooltip
                  contentStyle={{
                    backgroundColor: 'white',
                    border: '1px solid #E5E7EB',
                    borderRadius: '8px',
                    boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)'
                  }}
                />
              </PieChart>
            </ResponsiveContainer>
          </div>
        )}
      </div>

      {fullSize && (
        <div className="engagement-breakdown">
          <h4>Mode Breakdown</h4>
          <table className="breakdown-table">
            <thead>
              <tr>
                <th>Mode</th>
                <th>Games</th>
                <th>Percentage</th>
              </tr>
            </thead>
            <tbody>
              {modeData.map((item, index) => {
                const total = modeData.reduce((sum, d) => sum + d.games, 0);
                const percentage = ((item.games / total) * 100).toFixed(1);
                return (
                  <tr key={index}>
                    <td>
                      <span
                        className="color-dot"
                        style={{ backgroundColor: COLORS[item.mode] }}
                      ></span>
                      {item.mode}
                    </td>
                    <td>{item.games}</td>
                    <td>{percentage}%</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

export default GameEngagementChart;
